import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Activity, Eye, EyeOff } from "lucide-react";
import { trackLogin } from "@/lib/analytics";

export default function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  if (isAuthenticated) {
    const dest = user?.role === "cotizador" ? "/cotizador" : from;
    return <Navigate to={dest} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Completa todos los campos");
      return;
    }

    setLoading(true);
    /* Small delay to show loading state for demo feel */
    await new Promise((r) => setTimeout(r, 400));

    const ok = login(username.trim(), password);
    if (ok) {
      trackLogin(username.trim());
      // Check role from stored session to redirect cotizador users
      const session = JSON.parse(localStorage.getItem("uc_auth_session") || "{}");
      const dest = session.role === "cotizador" ? "/cotizador" : from;
      navigate(dest, { replace: true });
    } else {
      setError("Usuario o contraseña incorrectos");
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{
        background:
          "linear-gradient(135deg, #4c1d95 0%, #6d28d9 33%, #7c3aed 66%, #3b82f6 100%)",
      }}
    >
      <div className="w-full max-w-md bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8">
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="h-14 w-14 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-violet-400/40 mb-4"
            style={{
              background: "linear-gradient(135deg,#4c1d95,#7c3aed,#3b82f6)",
            }}
          >
            <Activity className="w-7 h-7" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            UC CHRISTUS
          </h1>
          <p className="text-sm text-slate-500 mt-1">Inteligencia del Paciente</p>
          <p className="text-[11px] text-slate-400 mt-1 max-w-xs text-center">
            Plataforma de inteligencia operacional para equipos de gestión clínica
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="login-username"
              className="block text-sm font-medium text-slate-700 mb-1.5"
            >
              Usuario
            </label>
            <input
              id="login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
              placeholder="Ingresa tu usuario"
            />
          </div>

          <div>
            <label
              htmlFor="login-password"
              className="block text-sm font-medium text-slate-700 mb-1.5"
            >
              Contraseña
            </label>
            <div className="relative">
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none transition"
                placeholder="Ingresa tu contraseña"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <p className="text-sm text-rose-600 bg-rose-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white shadow-md shadow-violet-300/40 transition disabled:opacity-60"
            style={{
              background:
                "linear-gradient(135deg,#4c1d95,#7c3aed,#3b82f6)",
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Ingresando…
              </span>
            ) : (
              "Iniciar sesión"
            )}
          </button>
        </form>
      </div>

      {/* Footer */}
      <p className="mt-8 text-xs text-white/60">
        © 2026 UC CHRISTUS — Inteligencia del Paciente
      </p>
    </div>
  );
}
